import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Typography,
  Divider,
  Tooltip,
  Fade,
  Slide
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  CalendarToday as BookingsIcon,
  Inventory as PackagesIcon,
  People as UsersIcon,
  LocalOffer as DealsIcon,
  Percent as PromotionsIcon,
  Category as CategoriesIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';

function Sidebar({ collapsed, onItemClick }) {
  const location = useLocation();
  const navigate = useNavigate();
  
  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: DashboardIcon, path: '/dashboard' },
    { id: 'bookings', label: 'Bookings', icon: BookingsIcon, path: '/bookings' },
    { id: 'packages', label: 'Packages', icon: PackagesIcon, path: '/packages' },
    { id: 'departure', label: 'Departure Date', icon: PackagesIcon, path: '/departure-date' },
    { id: 'users', label: 'Users', icon: UsersIcon, path: '/users' },
    { id: 'deals', label: 'Promo Code', icon: DealsIcon, path: '/deals' },
    { id: 'promotions', label: 'Promotions', icon: PromotionsIcon, path: '/promotions' },
    { id: 'categories', label: 'Categories', icon: CategoriesIcon, path: '/categories' },
    { id: 'settings', label: 'Settings', icon: SettingsIcon, path: '/settings' }
  ];

  const handleItemClick = (path) => {
    navigate(path);
    if (onItemClick) {
      onItemClick();
    }
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Logo Section */}
      <Box
        sx={{
          p: collapsed ? 2 : 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'flex-start',
          minHeight: 80,
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <Fade in timeout={300}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: collapsed ? 0 : 2,
            }}
          >
            <Avatar
              sx={{
                width: collapsed ? 40 : 48,
                height: collapsed ? 40 : 48,
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                border: '2px solid rgba(255, 255, 255, 0.2)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.25)',
                  transform: 'scale(1.05)',
                },
              }}
            >
              <img 
                src="/admin/mkm-logo.svg" 
                alt="MKM" 
                style={{ 
                  width: '70%', 
                  height: '70%', 
                  objectFit: 'contain',
                  filter: 'brightness(0) invert(1)'
                }}
              />
            </Avatar>
            {!collapsed && (
              <Slide direction="right" in={!collapsed} timeout={300}>
                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: 'white',
                      lineHeight: 1.2,
                    }}
                  >
                    MKM Tour
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.7)',
                      fontSize: '0.75rem',
                    }}
                  >
                    Admin Panel
                  </Typography>
                </Box>
              </Slide>
            )}
          </Box>
        </Fade>
      </Box>

      {/* Navigation Menu */}
      <Box sx={{ flexGrow: 1, py: 2 }}>
        <List sx={{ px: 1 }}>
          {menuItems.map((item, index) => {
            const IconComponent = item.icon;
            const active = isActive(item.path);
            
            return (
              <Fade
                key={item.id}
                in
                timeout={300 + (index * 50)}
              >
                <ListItem disablePadding sx={{ mb: 0.5 }}>
                  <Tooltip
                    title={collapsed ? item.label : ''}
                    placement="right"
                    arrow
                  >
                    <ListItemButton
                      onClick={() => handleItemClick(item.path)}
                      selected={active}
                      sx={{
                        minHeight: 48,
                        justifyContent: collapsed ? 'center' : 'initial',
                        px: collapsed ? 1.5 : 2,
                        py: 1.5,
                        borderRadius: 2,
                        mx: 1,
                        mb: 0.5,
                        color: 'rgba(255, 255, 255, 0.8)',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                          color: 'white',
                          transform: 'translateX(4px)',
                        },
                        '&.Mui-selected': {
                          backgroundColor: 'rgba(255, 255, 255, 0.15)',
                          color: 'white',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                          },
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            left: 0,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            width: 4,
                            height: '60%',
                            backgroundColor: 'white',
                            borderRadius: '0 4px 4px 0',
                          },
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 0,
                          mr: collapsed ? 0 : 2,
                          justifyContent: 'center',
                          color: 'inherit',
                        }}
                      >
                        <IconComponent sx={{ fontSize: 22 }} />
                      </ListItemIcon>
                      {!collapsed && (
                        <Slide direction="right" in={!collapsed} timeout={200}>
                          <ListItemText
                            primary={item.label}
                            sx={{
                              opacity: collapsed ? 0 : 1,
                              '& .MuiListItemText-primary': {
                                fontSize: '0.875rem',
                                fontWeight: active ? 600 : 500,
                              },
                            }}
                          />
                        </Slide>
                      )}
                    </ListItemButton>
                  </Tooltip>
                </ListItem>
              </Fade>
            );
          })}
        </List>
      </Box>

      {/* Footer */}
      {!collapsed && (
        <Box
          sx={{
            p: 2,
            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          }}
        >
          <Fade in={!collapsed} timeout={300}>
            <Typography
              variant="caption"
              sx={{
                color: 'rgba(255, 255, 255, 0.6)',
                fontSize: '0.75rem',
                textAlign: 'center',
                display: 'block',
              }}
            >
              © {new Date().getFullYear()}  MKM Tour
            </Typography>
          </Fade>
        </Box>
      )}
    </Box>
  );
}

export default Sidebar;